import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs';

import { UrlService } from '@core/services/url/url.service';

import * as Objects from '@core/objects';
import * as Access from '@app/models/access';
import { VersionFeedback, ShareVersionFeedback,ApprovalStatusCodeEnum } from '@core/models/version-feedback';

import { VersionShare, UploadVersionShare, FeedbackStatusCodeEnum, VersionShareEntryDetail } from '@core/models/version-share.model';
import { VersionShareFeedbackMessage } from '@core/models/version-share-message.model';
import { ApprovalActionPayload, ApprovalSignatureCredentials } from '@core/models/digital-signature.model';

import * as fromState from '@app/store/state/versions/versions.state';
import * as fromActions from '@app/store/state/versions/versions.actions';

@Injectable({
    providedIn: 'root'
})
export class VersionsViewService {

    constructor(
        private store: Store<fromState.Versions>,
        private actions$: Actions
    ) {}

    load = (id: number) => this.store.dispatch( new fromActions.LoadVersion(id));

    loadRelated = (link: string) => this.store.dispatch( new fromActions.LoadRelatedVersions(link));

    loadFeedbackFilesNotesCounter = () => this.store.dispatch( new fromActions.LoadVersionFeedbackNotesCounter());

    clear = () => this.store.dispatch( new fromActions.Clear());

    update = (id: number) => this.store.dispatch(new fromActions.UpdateVersion(id));
    
    updateMessages = (id: number) => this.store.dispatch(new fromActions.UpdateVersionMessages(id));

    // updateFeedbacks = (id: number, user: Objects.User) => this.store.dispatch(new fromActions.UpdateVersionFeedbacks(id, user));
    
    // 2026 june changed to assets. when leave a version file preview we need to clean version assets to open a new one
    // because assets of a previous version remain load when a new version is still loading
    // update 4th may 2022 ==> we need it to clear assets when version loaded is updatd to a status without access
    clearAssets = () => this.store.dispatch( new fromActions.ClearAssets());

    selectError = (key: string): Observable<HttpErrorResponse> => this.store.select(fromState.errorSelector(key));

    selectVersion = (): Observable<Objects.Version> => this.store.select(fromState.versionSelector);
    
    selectAssets = (): Observable<Objects.AmsFile[]> => this.store.select(fromState.versionAssetsSelector);

    selectRelatedVersions = (): Observable<Objects.Version[]> => this.store.select(fromState.versionRelatedsSelector);
    
    // loadMessages = () => this.store.dispatch( new fromActions.LoadVersionMessages());

    loadComments = () => this.store.dispatch( new fromActions.LoadVersionComments());

    // @deprecated oct-2026: legacy share feedback bubbles url call (GET /p/feedback/versions/feedbacks/{ids}), removed in the backend (answers 404). Use version_shares (LoadVersionShares)
    loadFeedbacks = () => this.store.dispatch( new fromActions.LoadVersionFeedbacks());

    // @deprecated oct-2026: legacy share feedback url call, use loadMyVersionShares
    loadShareFeedback = (user: Objects.User) => this.store.dispatch( new fromActions.LoadShareFeedback(user));

    // selectMessages = (): Observable<Objects.Message[]> => this.store.select(fromState.versionMessagesSelector);

    selectComments = (): Observable<Objects.VersionMessage[]> => this.store.select(fromState.versionCommentsSelector);
    
    // @deprecated oct-2026: legacy share feedback bubbles url call (GET /p/feedback/versions/feedbacks/{ids}), removed in the backend (answers 404). Use version_shares (LoadVersionShares)
    selectFeedbacks = (): Observable<VersionFeedback[]> => this.store.select(fromState.versionFeedbacksSelector);
   
    // @deprecated oct-2026: legacy share feedback url call, use selectMyVersionShares
    selectShareFeedback = (): Observable<ShareVersionFeedback> => this.store.select(fromState.versionShareFeedbackSelector);
    
    selectFeedbackFileNotesCounter = (): Observable<Objects.AnnotationsCounter[]> => this.store.select(fromState.versionFeedbackNotesCounterSelector);

    selectProject = (): Observable<Objects.Request> => this.store.select(fromState.versionProjectSelector);

    setStatus = (status: Objects.Status) => this.store.dispatch( new fromActions.SetVersionStatus(status));

    setLabels = (labels: Array<Objects.Label>) => {
        if(labels.length == 0)
            return;
        this.store.dispatch( new fromActions.SetVersionLabels(labels));
    }

    // Version actions and selectors for actions 
    // status management (used for display modals according actions in store)
    selectArchiveActionStatus = (): Observable<fromState.VersionsActionStatus> => this.store.select(fromState.actionArchiveStatusSelector);
    
    selectResetActionStatus = (): Observable<fromState.VersionsActionStatus> => this.store.select(fromState.actionResetStatusSelector);
    
    // to reset once modals are closed
    resetArchiveActionStatus = () => this.store.dispatch( new fromActions.ResetArchiveActionStatus());
    
    resetResetActionStatus = () => this.store.dispatch( new fromActions.ResetResetActionStatus());

    // dec-2023 introduce new VersionShare object in version details
    loadVersionShares = () => this.store.dispatch( new fromActions.LoadVersionShares());

    updateVersionShareBubbles = (id: number, user: Objects.User) => this.store.dispatch(new fromActions.UpdateVersionShares(id, user));

    loadUploadVersionShares = (token: string) => this.store.dispatch( new fromActions.LoadUploadVersionShares(token));

    selectVersionShares = (): Observable<VersionShare[]> => this.store.select(fromState.versionVersionSharesSelector);

    // oct-2026: open share entries (with their share) addressed to the current user on the loaded version

    selectMyVersionShares = (): Observable<VersionShareEntryDetail[]> => this.store.select(fromState.versionMyVersionSharesSelector);

    selectVersionShareMessages = (): Observable<VersionShareFeedbackMessage[]> => this.store.select(fromState.versionVersionShareMessagesSelector);

    selectCurrentUploadVersionShare = () :  Observable<UploadVersionShare> => this.store.select(fromState.versionUploadVersionSharesByVersionSelector);

    archiveVersionShare = (versionShare: VersionShare, dispatcherKey: string) => this.store.dispatch(new fromActions.ArchiveVersionShare(versionShare, dispatcherKey));

    resetVersionShare = (versionShare: VersionShare, dispatcherKey: string) => this.store.dispatch(new fromActions.ResetVersionShare(versionShare, dispatcherKey));

    // send files for new version when upload-version share was requested
    sendNewVersionFiles = (token:string, files: Array<string>) => this.store.dispatch( new fromActions.SendUploadVersionFiles(token, files));

    selectSendVersionFilesActionStatus = (): Observable<fromState.VersionsActionStatus> => this.store.select(fromState.actionSendVersionFilesSelector);

    resetUploadVersionFilesActionStatus = () => this.store.dispatch( new fromActions.ResetUploadVersionFilesActionStatus());

    // all closed version upload shares (for messages)
    selectClosedUploadVersionShares = () :  Observable<UploadVersionShare[]> => this.store.select(fromState.versionClosedUploadVersionSharesByVersionSelector);

    selectUploadedVersionUploads = () : Observable<Objects.Version[]> => this.store.select(fromState.versionUploadedVersionUploadsSelector); 

    sendSelfShareRequest = (postData: any ) => this.store.dispatch(new fromActions.SendSelfShareFeedbackRequest(postData));

    selectSelfShareRequestActionStatus = (): Observable<fromState.VersionsActionStatus> => this.store.select(fromState.actionSelfShareFeedbackRequestSelector);

    resetSelfShareRequestActionStatus = () => this.store.dispatch( new fromActions.ResetSelfShareFeedbackRequestActionStatus());

    // Refresh file signatures without full reload - updates only access.signature and access.expires_at
    refreshAssetsSignatures = () => this.store.dispatch( new fromActions.LoadVersionAssets());

}

@Injectable({
    providedIn: 'root'
})
export class VersionsEditorService {

    constructor(
        private store: Store<fromState.Versions>,
        private actions$: Actions
    ) {}

    selectAccessAll = (): Observable<Access.AccessVersion> => this.store.select(fromState.editorAccessAllSelector);

    selectAccess = (key: string): Observable<boolean> => this.store.select(fromState.editorAccessSelector(key));
}

@Injectable({
    providedIn: 'root'
})
export class VersionsService {

    constructor(
        protected url: UrlService, 
        protected http: HttpClient,
    ) {}

    getById$(id: string | number, prefixUrl: string = "") {
        return this.http
        .get(this.url.build(prefixUrl+"/versions/" + id));
    }

    getVersionsGroup$(groupLink: string,prefixUrl: string = "") {
        return this.http
        .get(this.url.build(prefixUrl+"/versions"),{params: {link: groupLink}});
    }

    /**
     * @deprecated oct-2026: legacy share feedback url call, removed in the backend (answers 404). No callers.
     */
    getVersionShareFeedback$(version:Objects.Version, user: Objects.User, prefixUrl: string = "") {
        return this.http
        .get(this.url.build(prefixUrl+"/p/feedback/versions/"+version.id+"/users/"+user.id));
    }
    // @deprecated oct-2026: legacy share feedback bubbles url call, removed in the backend (answers 404). Use VersionSharesService.getVersionShareItems$
    // // still used for load version feedbacks when version store is not used
    // getFeedbacks$(versions: Array<Objects.Version>, prefixUrl: string = "") {
    //     return this.http
    //     .get(this.url.build(prefixUrl+"/p/feedback/versions/feedbacks/"+versions.map((v: Objects.Version) => v.id).join('-')));
    // }

    giveFeedback$(version:Objects.Version, shareEntryHash: string, message: string, approve: boolean, credentials?: ApprovalSignatureCredentials, prefixUrl: string = "") {
        let options = { headers: new HttpHeaders({
            'Content-Type':  'application/json',
        })};

        let data: ApprovalActionPayload = {
            message: message,
            status: approve? ApprovalStatusCodeEnum.APPROVED_CODE : ApprovalStatusCodeEnum.REJECTED_CODE
        };
        if (credentials?.password){
            data.password = credentials.password;
        }
        if (credentials?.pin != null){
            data.pin = credentials.pin;
        }
        // new url without /p in jan-2024 after include response for optional cfr11 signature process
        return this.http.post(
            "/web/share/feedback/"+shareEntryHash+"/versions/"+version.id+"/approvals",
            data,
            options
        );
    }
    
    // completed action for feedbacks without approval 
    completeFeedback$(version:Objects.Version, shareEntryHash: string, pass?: string) {
        let options = {headers: new HttpHeaders({
            'Content-Type':  'application/json',
        })};

        let data: ApprovalActionPayload = { message: '', status: FeedbackStatusCodeEnum.CLOSED_CODE}; //2

        if (pass){
            data = {message: '', status: FeedbackStatusCodeEnum.CLOSED_CODE, password: pass};
        }

        return this.http.post(
            // this.url.build("/versions/"+version.id+"/approvals"),
            "/web/share/feedback/"+shareEntryHash+"/versions/"+version.id+"/feedbacks",
            data,
            options
        );
    }

    // ACTION TO APPROVE WITH SIGNATURE BY SSO NOT ON CFR11 (not used on jan-2024)
    signFeedback$(version:Objects.Version, shareEntryHash: string, message: string, approve: boolean, provider: string,  prefixUrl: string = "") {
        let options = {headers: new HttpHeaders({
            'Content-Type':  'application/json',
        })};

        let data: ApprovalActionPayload = {
            message: message,
            status: approve? ApprovalStatusCodeEnum.APPROVED_CODE : ApprovalStatusCodeEnum.REJECTED_CODE
        };

        return this.http.post(
            "/web/share/feedback/"+shareEntryHash+"/versions/"+version.id+"/approvals/oauth2/"+provider,
            data,
            options
        );
    }

    // for version add because we have no version load in the store
    getAllStatus$(prefixUrl: string = "") {
        return this.http
            .get(this.url.build(prefixUrl+"/versions/status"));
    }
    // status to update
    getNextStatus$(id: string | number, prefixUrl: string = "") {
        return this.http
        .get(this.url.build(prefixUrl+"/versions/"+ id + "/status-next"));
    }

    setVersionsGroupLabels$(versions: Objects.Version[],labels: Objects.Label[], prefixUrl: string = "") {
        return this.http
        .put(this.url.build(prefixUrl+"/versions/labels/"+versions.map((v: Objects.Version) => v.id).join('-')),{labels: labels.map((l: Objects.Label) => l.id)});
    }

    // @deprecated
    getProject$(version: Objects.Version, prefixUrl: string = "") {
        return this.http
        .get<Objects.Request>(this.url.build(prefixUrl+"/versions/" + version.id + "/request"));
    }
    
    setStatus$(version: Objects.Version, status: Objects.Status, prefixUrl: string = "") {
        let options = {headers: new HttpHeaders({
            'Content-Type':  'application/json',
        })};
        return this.http.put(
            this.url.build(prefixUrl+"/versions/"+version.id),
            { status: status.id },
            options
        );
    }

    getAccess$(version: Objects.Version, prefixUrl: string = "") {
        return this.http
        .get<Access.AccessVersion>(this.url.build(prefixUrl+"/versions/" + version.id + "/permissions"));
    
    }

    loadFeedbackMessages$(version: Objects.Version, prefixUrl: string = "") {
        return this.http
        .get(this.url.build(prefixUrl+"/p/feedback/versions/"+version.id+"/messages"));
    }

    sendFeedbackReminder$(versionItems:Objects.Version[], prefixUrl: string = "") {
        let options = {headers: new HttpHeaders({
            'Content-Type':  'application/json',
        })};

        let data: any = {
            versions: versionItems.map((v:Objects.Version) => v.id)
        };

        return this.http.post(
            this.url.build("/versions/approvals/reminders"),
            data,
            options
        );
    }

}
